import React, { memo, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import moment from 'moment/moment';

import { Input } from 'src/components/helpers/Input';
import Checkbox from 'src/components/helpers/Checkbox';
import Textarea from 'src/components/helpers/Textarea';
import { unwrap } from 'src/utils/ChangeSpy';
import { dayToString } from 'src/utils/dateFormat';
import Button from 'src/components/helpers/Button';

import { RestartAgreements } from './RestartAgreements';
import { OneOffAgreements } from './OneOffAgreements';
import { BlanketAgreements } from './BlanketAgreements';
import { FilesPanel } from './FilesPanel';
import { AgreementCategories } from './AgreementCategories';

import style from './style.module.css';

const RetainerComponent = ({
  agreementFiles,
  isHighestStatus,
  isReadOnly,
  retainer: root,
  onAddRetainer,
  onChange,
  onFindTargets,
  onSelectAgreementCategories,
  onAgreementLink,
  onAgreementUnlink,
  onSuggestionsChange,
  onDelete,
  onDeleteFile,
  onUploadFile,
}) => {
  const startDate = unwrap(root.get('startDate'));
  const originalAgreementDate = moment.isDate(startDate) ? `- ${dayToString(startDate)}` : null;
  const originalAgreementName = isHighestStatus ? 'Original Retainer Agreement' : 'Original Agreement';
  const companyRetainers = root
    .get('companyRetainers')
    .filter(retainer => unwrap(retainer.get('deleteType')) === undefined);
  const agreementFilesOriginal = agreementFiles.filter(file => file.get('companyRetainerId') === null);
  const agreementCategories = unwrap(root.get('agreementCategories'));

  useEffect(() => {
    const scrollToAgreementSection = document.querySelector(
      window.location.hash === '' ? null : document.location.hash,
    );

    if (scrollToAgreementSection) {
      scrollToAgreementSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  return (
    <div className={classNames(style.host, 'tab-pane active tab-target full-height')} id="buy-slide-6">
      <div className="col-md-12 full-height">
        <div>
          {isHighestStatus ? (
            <Button className="btn-default mr16" disabled={isReadOnly} onClick={() => onAddRetainer('retainer')}>
              Restart
            </Button>
          ) : (
            <>
              <Button className="btn-default mr16" disabled={isReadOnly} onClick={() => onAddRetainer('one-off')}>
                One-Off
              </Button>
              <Button disabled={isReadOnly} onClick={() => onAddRetainer('blanket')}>
                Blanket
              </Button>
            </>
          )}
        </div>
        <form className={style.form} onChange={onChange}>
          <RestartAgreements
            files={agreementFiles}
            isReadOnly={isReadOnly || !isHighestStatus}
            onAgreementLink={onAgreementLink}
            onAgreementUnlink={onAgreementUnlink}
            onChange={onChange}
            onDelete={onDelete}
            onDeleteFile={onDeleteFile}
            onSelectAgreementCategories={onSelectAgreementCategories}
            onUploadFile={onUploadFile}
            retainers={companyRetainers}
          />
          <OneOffAgreements
            files={agreementFiles}
            isReadOnly={isReadOnly}
            onAgreementLink={onAgreementLink}
            onAgreementUnlink={onAgreementUnlink}
            onChange={onChange}
            onDelete={onDelete}
            onDeleteFile={onDeleteFile}
            onFindTargets={onFindTargets}
            onSelectAgreementCategories={onSelectAgreementCategories}
            onSuggestionsChange={onSuggestionsChange}
            onUploadFile={onUploadFile}
            root={root}
          />
          <BlanketAgreements
            isReadOnly={isReadOnly}
            onChange={onChange}
            onDelete={onDelete}
            retainers={companyRetainers}
          />
          <div className={style.item}>
            <div className={style.titleWrap}>
              <h5 className={style.title}>
                {originalAgreementName} {originalAgreementDate}
              </h5>
            </div>
            <div className="row">
              <div className="col-xs-12 col-sm-5 col-md-5">
                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-md-6">
                    <div className="form-group">
                      <Input
                        className="input-sm"
                        disabled={isReadOnly}
                        label="Contractual Name"
                        name="contractualName"
                        onChange={onChange}
                        root={root}
                        deep
                        showLabel
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-sm-6 col-md-6">
                    <div className="form-group">
                      <Input
                        className="input-sm"
                        disabled={isReadOnly}
                        label="Monthly Retainer"
                        name="retainer"
                        root={root}
                        type="money"
                        deep
                        showLabel
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-md-6">
                    <div className="form-group">
                      <Input
                        className="input-sm"
                        defaultValue={null}
                        disabled={isReadOnly}
                        label="Start Date"
                        name="startDate"
                        onChange={onChange}
                        placeholder="Start Date"
                        root={root}
                        title="This is the date, per the contract, that the project kicks off"
                        type="date"
                        deep
                        showLabel
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-sm-6 col-md-6">
                    <div className="form-group">
                      <Input
                        className="input-sm"
                        defaultValue={null}
                        disabled={isReadOnly}
                        label="End Date"
                        name="endDate"
                        onChange={onChange}
                        placeholder="End Date"
                        root={root}
                        title="This is the date the agreement officially terminates"
                        type="date"
                        deep
                        showLabel
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-md-6">
                    <div className="form-group">
                      <Input
                        className="input-sm"
                        defaultValue={null}
                        disabled={isReadOnly}
                        label="Notification to Start Date"
                        name="notificationStartDate"
                        onChange={onChange}
                        placeholder="Notification to Start Date"
                        root={root}
                        title="This is the date we were informed of the client's intent to start"
                        type="date"
                        deep
                        showLabel
                      />
                    </div>
                  </div>
                  <div className="col-xs-12 col-sm-6 col-md-6">
                    <div className="form-group">
                      <Input
                        className="input-sm"
                        defaultValue={null}
                        disabled={isReadOnly}
                        label="Notification to Cancel Date"
                        name="notificationCancellationDate"
                        onChange={onChange}
                        placeholder="Notification to Cancel Date"
                        root={root}
                        title="This is the date that the client informed us of their intent to cancel"
                        type="date"
                        deep
                        showLabel
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    <span className="field-label">Tail</span>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    <div className={classNames(style.tail, 'row')}>
                      <div className="col-xs-4">
                        <div className="form-group d-flex">
                          <Input
                            className={classNames(style.tailMonthsInput, 'input-sm')}
                            disabled={isReadOnly}
                            id="tail-months"
                            min="0"
                            name="tailMonths"
                            onChange={onChange}
                            root={root}
                            type="number"
                            deep
                          />
                          <label htmlFor="tail-months">Months from</label>
                        </div>
                      </div>
                      <div className="col-xs-4">
                        <Checkbox
                          deep="checked"
                          disabled={isReadOnly}
                          id="tail-last-introduced"
                          label="Last Introduced"
                          name="tailLastIntroduced"
                          onChange={onChange}
                          root={root}
                        />
                      </div>
                      <div className="col-xs-4">
                        <Checkbox
                          deep="checked"
                          disabled={isReadOnly}
                          id="tail-termination"
                          label="Termination"
                          name="tailTermination"
                          onChange={onChange}
                          root={root}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    <Textarea
                      disabled={isReadOnly}
                      label="Notes"
                      name="retainerNote"
                      onChange={onChange}
                      placeholder=""
                      root={root}
                      rows="4"
                      deep
                      showLabel
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12 mt10">
                    <FilesPanel
                      files={agreementFilesOriginal}
                      onDeleteFile={onDeleteFile}
                      onUploadFile={event => onUploadFile(event, null)}
                    />
                  </div>
                </div>
              </div>
              <div className="col-xs-12 col-sm-7 col-md-7">
                <AgreementCategories
                  agreementCategories={agreementCategories}
                  onAgreementLink={selectedAgreementCategory =>
                    onAgreementLink({ agreementCategories, type: 'agreementCategories', selectedAgreementCategory })
                  }
                  onAgreementUnlink={agreementCategories =>
                    onAgreementUnlink({ agreementCategories, type: 'agreementCategories' })
                  }
                  onSelectAgreementCategories={() =>
                    onSelectAgreementCategories(agreementCategories, 'agreementCategories')
                  }
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

RetainerComponent.propTypes = {
  isHighestStatus: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  onAddRetainer: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFindTargets: PropTypes.func.isRequired,
  onSuggestionsChange: PropTypes.func.isRequired,
  retainer: PropTypes.instanceOf(Map).isRequired,
};

/** Retainer component. */
export const Retainer = memo(RetainerComponent);
